/** @jsxImportSource @emotion/react */
import React, { useEffect, useContext, useRef, useState, useMemo } from 'react';
import css from '@emotion/css';
import useTranslations from '../../hooks/useTranslations';
import styled from 'styled-components/macro';
import WarningIcon from '@material-ui/icons/WarningTwoTone';
import IconButton from '@material-ui/core/IconButton';
import FormGroup from '@material-ui/core/FormGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import CloseIcon from '@material-ui/icons/Close';
import moment from '../../utils/momentWithLocale';
import {
  setValue as setLSValue,
  getValue as getLSValue
} from '../../utils/LocalStorageUtils';
import Link from '../Link';
import Animation from '../Animation';
import { FormContext } from './FormContext';
import fetchAPI from '../../utils/fetchAPI';
import DontPrint from '../../internal-components/DontPrint';
import { TextString } from '../Text';
import getJWT from '../../utils/getJWT';

const StyledCheckbox = styled(Checkbox)`
  && svg {
    font-size: 18px !important;
  }
`;

const WarningMessage = styled.div`
  background: rgba(254, 180, 78, 0.1);
  display: flex;
  align-items: stretch;
  justify-content: flex-start;
  padding: 8px;
  border: 1px solid rgba(254, 180, 78, 0.5);
  border-radius: 4px;
  margin: 2rem 1rem 0;

  & > div {
    flex: 1;
    padding: 8px;
  }

  .WM__big-icon {
    font-size: 32px;
    color: #feb44e;
  }
`;

const formatDate = date => moment.utc(+date).format('LL');

const WorkInProgressBanner = ({ submission: givenSubmission = {} }) => {
  const { formId } = useContext(FormContext);
  const isFetchedSubmmission = useRef(false);
  // const status = submission.statusData && submission.statusData.status;
  const [fetchedSubmission, setFetchedSubmission] = useState({});

  const submission = useMemo(() => {
    if (JSON.stringify(givenSubmission) === '{}') {
      isFetchedSubmmission.current = true;
      return fetchedSubmission;
    }
    isFetchedSubmmission.current = false;

    return givenSubmission;
  }, [fetchedSubmission, givenSubmission]);

  useEffect(() => {
    if (JSON.stringify(givenSubmission) !== '{}') {
      return;
    }
    fetchAPI(
      `/forms/v1/eusubmissions/` +
        `?limit=1` +
        `&sort='metadata'->'updatedDate' desc` +
        `&query=[[{"field": "'formId'", "op": "=", "value": "${formId}" }]]`
    ).then(r => {
      setFetchedSubmission(r.results[0] || {});
    });
  }, []);

  return Object.keys(submission).length > 0 ? (
    isFetchedSubmmission.current ? (
      <Msg1 submission={submission} />
    ) : (
      <Msg2 submission={submission} />
    )
  ) : null;
};

const Msg1 = ({ submission = {} }) => {
  const [showMe, setShowMe] = useState(
    getLSValue('is-info-msg-hidden-1') !== 'true'
  );
  const updated = submission.metadata && submission.metadata.updatedDate;
  const submitted = submission.submissionDate;

  return (
    <DontPrint>
      <Animation in={showMe} type="collapse">
        <WarningMessage>
          <div style={{ flex: '0 1 32px' }}>
            <WarningIcon className="WM__big-icon" />
          </div>
          <div>
            <IconButton
              style={{ float: 'right' }}
              onClick={() => setShowMe(false)}
            >
              <CloseIcon />
            </IconButton>

            <p>
              {submitted
                ? `You submitted a response to this form on ${formatDate(
                    submitted
                  )}. `
                : `You started working on a response to this form` +
                  (updated ? ` on ${formatDate(updated)}` : '') +
                  '. '}
            </p>
            <p>
              <Link
                to={`/forms/assigned/${submission.formId}/${submission.id}`}
              >
                <b>
                  {submitted
                    ? 'Click here to view your submission.'
                    : 'Click here to continue your saved progress.'}
                </b>
              </Link>
            </p>
            <FormGroup>
              <FormControlLabel
                control={
                  <StyledCheckbox
                    onChange={e => {
                      const checked = e.target.checked;
                      setLSValue('is-info-msg-hidden-1', checked);
                      const j = getJWT();
                      const currentUserId = j ? j.userId : '';
                      if (currentUserId) {
                        const code = 'is-info-msg-hidden-1-' + currentUserId;
                        fetchAPI('/configurations/v1/userpreference', {
                          method: 'POST',
                          body: {
                            code,
                            configName: 'sort',
                            module: 'forms',
                            productID: 'omnitrack',
                            value: checked ? '1' : ''
                          }
                        });
                      }
                    }}
                    name="hideMessage"
                    data-testid="hide-this-msg"
                    color="primary"
                  />
                }
                label={
                  <small>
                    <TextString content="entries.forms.hideMessage" />
                  </small>
                }
              />
            </FormGroup>
          </div>
        </WarningMessage>
      </Animation>
    </DontPrint>
  );
};

const Msg2 = ({ submission = {} }) => {
  const submitted = submission.submissionDate;
  const { form } = useContext(FormContext);
  const currentStatus = submission.statusData.status;
  const statusDef = form.customStatuses.find(
    s => s.internalId === currentStatus
  );
  const isStatusLocked = !!statusDef.lockRules;

  const [showMe, setShowMe] = useState(
    getLSValue('is-info-msg-hidden-2') !== 'true'
  );
  const t = useTranslations();
  if (!submitted) {
    return null;
  }
  return (
    <DontPrint>
      <Animation in={showMe} type="collapse">
        <WarningMessage>
          <div style={{ flex: '0 1 32px' }}>
            <WarningIcon className="WM__big-icon" />
          </div>
          <div>
            <IconButton
              style={{ float: 'right' }}
              onClick={() => setShowMe(false)}
            >
              <CloseIcon />
            </IconButton>

            <p
              css={css`
                font-weight: bold;
              `}
            >
              {t('forms.you_submitted', {
                date: formatDate(submission.submissionDate)
              })}
            </p>
            <p
              css={css`
                font-weight: bold;
              `}
            >
              {isStatusLocked ? t('forms.locked') : t('forms.not_locked')}
            </p>
            <FormGroup>
              <FormControlLabel
                control={
                  <StyledCheckbox
                    onChange={e => {
                      const checked = e.target.checked;
                      setLSValue('is-info-msg-hidden-2', checked);
                      const j = getJWT();
                      const currentUserId = j ? j.userId : '';
                      if (currentUserId) {
                        const code = 'is-info-msg-hidden-2-' + currentUserId;
                        fetchAPI('/configurations/v1/userpreference', {
                          method: 'POST',
                          body: {
                            code,
                            configName: 'sort',
                            module: 'forms',
                            productID: 'omnitrack',
                            value: checked ? '1' : ''
                          }
                        });
                      }
                    }}
                    name="hideMessage"
                    data-testid="hide-this-msg"
                    color="primary"
                  />
                }
                label={
                  <small>
                    <TextString content="entries.forms.hideMessage" />
                  </small>
                }
              />
            </FormGroup>
          </div>
        </WarningMessage>
      </Animation>
    </DontPrint>
  );
};

export default WorkInProgressBanner;
